// Content for "B-BBEE Awareness: Half-Day Introduction", condensed from the
// reading-lesson course of the same id in server.js into a live-style slide
// deck for facilitator-led delivery, matching the presentation system used by
// "Claude for Professionals" and the "B-BBEE Masterclass".
//
// This is a beginner-level, half-day session — deliberately shorter and
// shallower than the full-day Masterclass or the 3-day Professional course.
// No B-BBEE facts have been added beyond what the source course already
// states; regulatory specifics (turnover bands, point weightings, status
// level detail) keep the source's own hedged language rather than asserting
// numbers.

import { applyOkiruTemplate } from '../okiruPresentationTemplate';

export const manifest = applyOkiruTemplate({
  id: 'bbbee-awareness-halfday',
  title: 'B-BBEE Awareness: Half-Day Introduction',
  subtitle: 'Okiru Learn',
  modules: [
    { index: 1, name: 'Why B-BBEE Exists' },
    { index: 2, name: 'Categories, Certificates and Status Levels' },
    { index: 3, name: 'The Scorecard at a Glance' },
    { index: 4, name: 'Common Misconceptions and What Fronting Actually Is' },
    { index: 5, name: 'Your Role and Where to Go Next' },
  ],
}, 'okiru-light');

export const slides = [
  {
    id: 1, layout: 'title',
    kicker: 'Okiru Learn · Half-day introduction',
    title: 'B-BBEE Awareness: Half-Day Introduction',
    lead: 'A half-day introduction to B-BBEE for anyone in an organisation who needs baseline literacy: why it exists, the scorecard at a glance, common misconceptions, and where to go next.',
    meta: ['5 modules · approx. 4 hours', 'Beginner level', 'Okiru Learn'],
  },
  {
    id: 2, layout: 'statement',
    text: 'By the end of this session, you can explain why B-BBEE exists, recognise the seven scorecard elements at a glance, spot the most common myths and fronting risks, and know exactly where to go for deeper training.',
    footnote: 'That is the whole job of an awareness-level session: enough literacy to never accidentally work against your organisation’s transformation goals.',
  },
  {
    id: 3, layout: 'agenda',
    kicker: 'The journey', title: 'Five modules, one half-day',
    columns: [
      [
        { time: '01', label: 'Why B-BBEE Exists' },
        { time: '02', label: 'Categories, Certificates and Status Levels' },
        { time: '03', label: 'The Scorecard at a Glance' },
      ],
      [
        { time: '04', label: 'Common Misconceptions and Fronting' },
        { time: '05', label: 'Your Role and Where to Go Next' },
      ],
    ],
  },

  // ---- Module 1: Why B-BBEE Exists ----
  {
    id: 4, layout: 'intro', module: 1,
    kicker: 'Module 01', title: 'Why B-BBEE Exists',
    lead: 'Before looking at any scorecard mechanics, it helps to understand the specific problem B-BBEE was built to address.',
    outcome: 'By the end you can explain in plain terms why B-BBEE was introduced and what problem it is trying to solve.',
    templateAsset: 'workshop-facilitator', imageChrome: 'none',
  },
  {
    id: 5, layout: 'lead', module: 1,
    kicker: 'Module 01', title: 'A legacy of exclusion',
    lead: 'Before 1994, access to ownership, capital, senior jobs, formal skills training, and government and corporate contracts was systematically restricted along racial lines. That legacy did not disappear when the political system changed.',
    body: 'Black South Africans remained, on average, far less likely to own equity in the formal economy, sit on boards, hold senior management roles, or run established suppliers to large companies. B-BBEE is the policy response to that specific problem: political inclusion without economic inclusion is unstable, and for a business operating in South Africa it is also a real commercial risk.',
  },
  {
    id: 6, layout: 'lead', module: 1,
    kicker: 'Module 01', title: 'From voluntary charters to binding law',
    lead: 'South Africa first tried voluntary transformation charters in the early 2000s, sector by sector. Voluntary charters produced inconsistent, hard-to-compare results, so government moved to a single, legislated framework: the Broad-Based Black Economic Empowerment Act, first passed in 2003 and substantially amended in 2013.',
    callout: { label: 'Always confirm', text: 'Confirm the current gazetted Codes of Good Practice and any applicable sector code before relying on a specific figure or clause number.' },
  },
  {
    id: 7, layout: 'lead', module: 1,
    kicker: 'Module 01', title: 'Why status became a commercial issue',
    lead: 'The shift from "voluntary" to "legislated and measured" changed the incentives. A company’s B-BBEE status level now affects its ability to win government and many private-sector contracts, its standing with lenders and investors, and increasingly its reputation with customers and employees.',
  },
  {
    id: 8, layout: 'lead', module: 1,
    kicker: 'Module 01', title: 'Business risk, not just social policy',
    lead: 'Treat B-BBEE as a business-risk and market-access topic, not only a social topic.',
    body: 'A poor status level can close doors that have nothing to do with abstract race relations: tender eligibility, funding conditions, and customer procurement policies.',
  },
  {
    id: 9, layout: 'exercise', module: 1,
    kicker: 'Module 01 · Activity', meta: 'Group discussion', title: 'Name one visible way your team reflects South Africa’s demographics — or does not',
    steps: [
      { title: 'Discuss', detail: 'Name one visible way your own department or team reflects, or does not reflect, South Africa’s demographics.' },
    ],
    checkFor: 'You can state, in one sentence, why B-BBEE exists: political freedom in 1994 did not automatically bring economic participation for the black majority.',
  },

  // ---- Module 2: Categories, Certificates and Status Levels ----
  {
    id: 10, layout: 'intro', module: 2,
    kicker: 'Module 02', title: 'Categories, Certificates and Status Levels',
    lead: 'Not every organisation is measured the same way. Before anything else, it needs to know which category it falls into and what kind of evidence that category requires.',
    outcome: 'By the end you can identify the three broad enterprise-size categories under the generic Codes and understand, at a high level, how a B-BBEE status level is arrived at.',
    templateAsset: 'team-collaboration', imageChrome: 'none',
  },
  {
    id: 11, layout: 'lead', module: 2,
    kicker: 'Module 02', title: 'Not every organisation is measured the same way',
    lead: 'The generic Codes of Good Practice group organisations broadly into Exempted Micro Enterprises (EME), Qualifying Small Enterprises (QSE), and Generic (large) Enterprises, based on annual turnover.',
    callout: { label: 'Always confirm', text: 'The exact turnover bands are set out in the gazetted Codes and do get updated, so always confirm the current thresholds and any applicable sector code rather than relying on a remembered number.' },
  },
  {
    id: 12, layout: 'cards', module: 2,
    kicker: 'Module 02', title: 'Three broad categories, three different journeys',
    columns: 3,
    cards: [
      { title: 'EME (smallest)', description: 'Usually a sworn affidavit, no full scorecard. Often receives an automatically favourable status level if majority black-owned.' },
      { title: 'QSE (small-to-medium)', description: 'Affidavit for qualifying black-owned entities, or a simplified scorecard covering a reduced set of elements — confirm current requirements.' },
      { title: 'Generic / Large Enterprise', description: 'Full independent verification against the complete generic scorecard across all elements.' },
    ],
  },
  {
    id: 13, layout: 'keyterm', module: 2,
    kicker: 'Key term', term: 'Status Level',
    definition: 'Once measured, an organisation is placed on a status level scale, running roughly from Level 1 (best) down through several intermediate levels to "non-compliant".',
    body: 'A higher level generally earns the organisation’s customers a larger notional B-BBEE procurement recognition when they buy from it — which is exactly why status level matters to a supplier trying to win business from large corporates or government.',
    callouts: [
      { label: 'It expires', text: 'A certificate or affidavit is only valid for a defined measurement period. Letting it lapse quietly removes a supplier from a customer’s preferred list even if nothing else has changed.' },
    ],
  },
  {
    id: 14, layout: 'lead', module: 2,
    kicker: 'Module 02', title: 'Get the category right first',
    lead: 'If you only remember one thing from this module: your category determines your obligations, and your obligations determine your evidence. Get the category right first.',
  },
  {
    id: 15, layout: 'exercise', module: 2,
    kicker: 'Module 02 · Activity', meta: 'Individual research', title: 'Find your own organisation’s category',
    steps: [
      { title: 'Investigate', detail: 'Find out, or make your best estimate of, which size category your own organisation falls into and how it currently proves its B-BBEE status.' },
    ],
    checkFor: 'Every organisation’s B-BBEE journey starts with correctly identifying its size category, because that determines what it needs to prove and how.',
  },

  // ---- Module 3: The Scorecard at a Glance ----
  {
    id: 16, layout: 'intro', module: 3,
    kicker: 'Module 03', title: 'The Scorecard at a Glance',
    lead: 'This module stays at the level of what each element is trying to do, not how many points it is worth or how it is calculated. That depth belongs to Okiru’s full-day masterclass and 3-day professional course.',
    outcome: 'By the end you can recognise the seven elements of the generic scorecard and describe, in one sentence each, what every element is broadly trying to achieve.',
    templateAsset: 'thinking-learner', imageChrome: 'none',
  },
  {
    id: 17, layout: 'numbered', module: 3,
    kicker: 'Module 03', title: 'The seven elements, in plain language',
    items: [
      { title: 'Ownership', description: 'Who genuinely holds equity, voting rights and economic benefit in the business.' },
      { title: 'Management Control', description: 'How representative leadership and decision-making are, from the board down.' },
      { title: 'Skills Development', description: 'How much the organisation invests in training and developing black employees and learners.' },
      { title: 'Preferential Procurement', description: 'How much of the organisation’s spend goes to B-BBEE-recognised suppliers.' },
      { title: 'Supplier Development', description: 'How the organisation actively strengthens black-owned suppliers it already buys from.' },
      { title: 'Enterprise Development', description: 'How the organisation helps grow black-owned businesses outside its current supply chain.' },
      { title: 'Socio-Economic Development', description: 'What the organisation contributes toward broader community and economic access.' },
    ],
  },
  {
    id: 18, layout: 'comparison', module: 3,
    kicker: 'Module 03', title: 'Two groupings, one purpose',
    columns: [
      { heading: 'Inside the organisation', items: ['Ownership — who holds power', 'Management Control — who decides', 'Skills Development — who gets developed'] },
      { heading: 'Outside the organisation', items: ['Preferential Procurement — who gets paid', 'Supplier Development — who gets strengthened', 'Enterprise Development — who gets grown'] },
    ],
    callout: { label: 'Socio-Economic Development', text: 'Sits alongside both groupings, reaching people who may never be an employee or a supplier at all.' },
    footnote: 'The relative weighting of each element differs for large enterprises and QSEs, and the applicable weightings do get updated in the gazetted Codes, so treat any specific point value you hear as something to confirm, not memorise, at this stage.',
  },
  {
    id: 19, layout: 'numbered', module: 3,
    kicker: 'Module 03', title: 'How the seven elements typically get managed',
    items: [
      { title: 'Someone is accountable', description: 'A named owner, often supported by a B-BBEE committee, tracks all seven elements together rather than each in isolation.' },
      { title: 'Data gets collected continuously', description: 'Payroll, supplier, training and spend data feed the scorecard all year, not only before a verification date.' },
      { title: 'A verification agency checks the evidence', description: 'An accredited agency reviews the organisation’s evidence and issues the certificate or confirms the affidavit basis.' },
      { title: 'The certificate gets used and renewed', description: 'Customers and tender processes rely on it until the next measurement period begins.' },
    ],
  },
  {
    id: 20, layout: 'lead', module: 3,
    kicker: 'Module 03', title: 'You do not need to master calculations',
    lead: 'You do not need to master calculations to be useful here. Simply knowing which of the seven elements a decision touches, before it is made, is often enough to avoid an avoidable mistake.',
  },
  {
    id: 21, layout: 'exercise', module: 3,
    kicker: 'Module 03 · Activity', meta: 'Individual mapping', title: 'Spot the seven elements in your own organisation',
    steps: [
      { title: 'Map it', detail: 'For each of the seven elements, name one activity already happening somewhere in your organisation that plausibly touches it.' },
    ],
    checkFor: 'The scorecard is not one test: it is seven different levers, and most organisations already have partial progress on several of them without realising it.',
  },

  // ---- Module 4: Common Misconceptions and What Fronting Actually Is ----
  {
    id: 22, layout: 'intro', module: 4,
    kicker: 'Module 04', title: 'Common Misconceptions and What Fronting Actually Is',
    lead: 'Awareness-level training exists partly to shut down bad information before it turns into bad decisions.',
    outcome: 'By the end you can correct the most common misunderstandings about B-BBEE circulating in most workplaces, and describe, in plain terms, what fronting is.',
    templateAsset: 'conversation-coaching', imageChrome: 'none',
  },
  {
    id: 23, layout: 'lead', module: 4,
    kicker: 'Module 04', title: 'Most misunderstanding comes from two extremes',
    lead: 'Most B-BBEE misunderstanding comes from treating it as either a race-based tax or a pure paperwork exercise, when it is a legislated economic-participation framework with real legal consequences for getting it wrong.',
    body: 'This module covers the misconceptions Okiru sees most often, and what fronting actually means.',
  },
  {
    id: 24, layout: 'comparison', module: 4,
    kicker: 'Module 04', title: 'Myth vs reality',
    columns: [
      { heading: 'Myth', items: [
        '"B-BBEE is just a tax on white-owned businesses."',
        '"A high score means the company is definitely transforming for real."',
        '"Once we get our certificate, we are done until next year."',
        '"Adding a black shareholder on paper is enough for Ownership points."',
        '"Fronting is just aggressive tax planning, everyone does it."',
      ] },
      { heading: 'Reality', items: [
        'B-BBEE measures ownership, jobs, skills, procurement and community investment. Any business, of any ownership profile, can improve its score through genuine transformation activity.',
        'A score reflects what was measured and evidenced in one period. It does not guarantee the underlying activity is sustainable or that the intent behind it was genuine.',
        'B-BBEE performance is built from data generated all year: payroll, spend, training, supplier records. Waiting until the measurement date to start is what creates last-minute fronting risk.',
        'Ownership points depend on real voting rights, real economic benefit and, over time, real net value, not a name on a share register alone.',
        'Fronting is conduct that misrepresents or undermines the actual objectives of the B-BBEE Act, and it carries serious legal consequences for the organisation and individuals involved.',
      ] },
    ],
  },
  {
    id: 25, layout: 'keyterm', module: 4,
    kicker: 'Key term', term: 'Fronting',
    definition: 'Any arrangement, transaction or way of operating that is designed to make an organisation look more transformed on paper than it actually is in practice.',
    body: 'Common patterns include a black shareholder, director or manager who exists on paper but has no real say in decisions or access to profit; a supplier that is technically black-owned but the underlying business, price and margin are effectively still controlled elsewhere; and community or development spend that never actually reaches the people it is supposed to benefit.',
    callouts: [
      { label: 'Why it matters', text: 'Fronting defeats the purpose of the whole framework: it lets an organisation claim the benefit of transformation, such as better tender access or a better status level, without doing the underlying work.' },
    ],
  },
  {
    id: 26, layout: 'lead', module: 4,
    kicker: 'Module 04 · Warning', title: 'If it looks designed to create appearance, escalate it',
    lead: 'Fronting carries real legal consequences under the B-BBEE Act, including for individuals involved, in addition to the reputational and commercial damage of being caught.',
    body: 'If a structure or arrangement looks designed to create the appearance of compliance rather than the substance of it, escalate it. Do not assume it is someone else’s problem.',
  },
  {
    id: 27, layout: 'exercise', module: 4,
    kicker: 'Module 04 · Activity', meta: 'Individual reflection', title: 'Which myth have you heard at work?',
    steps: [
      { title: 'Reflect', detail: 'Read the five myth-and-reality pairs and identify which myth you have personally heard repeated at work.' },
    ],
    checkFor: 'You can name, out loud, which myth you have heard and what the more accurate reality is.',
  },

  // ---- Module 5: Your Role and Where to Go Next ----
  {
    id: 28, layout: 'intro', module: 5,
    kicker: 'Module 05', title: 'Your Role and Where to Go Next',
    lead: 'The point of a half-day introduction is not to make you a B-BBEE practitioner. It is to make sure you never accidentally work against your organisation’s transformation goals, and that you know where to go if you want to go deeper.',
    outcome: 'By the end you can identify realistic, appropriate actions someone at awareness level can take, and understand Okiru’s recommended B-BBEE learning pathway.',
    templateAsset: 'take-action', imageChrome: 'none',
  },
  {
    id: 29, layout: 'lead', module: 5,
    kicker: 'Module 05', title: 'Awareness is the foundation',
    lead: 'Awareness is the foundation. Deeper scorecard mechanics and audit-readiness are covered in Okiru’s full-day B-BBEE Masterclass, and end-to-end ownership of a company’s B-BBEE compliance is covered in the 3-day B-BBEE Professional course.',
  },
  {
    id: 30, layout: 'comparison', module: 5,
    kicker: 'Module 05', title: 'What you can realistically do',
    columns: [
      { heading: 'Do this', items: [
        'Flag a supplier decision to whoever tracks procurement recognition, before it is finalised',
        'Keep accurate records for any training, learnership or community activity you are involved in',
        'Raise a genuine concern about a structure that looks built to create appearance rather than substance',
        'Stop repeating the myths from Module 4 as if they were fact',
      ] },
      { heading: 'Not this', items: [
        'Attempt to interpret the Codes yourself',
        'Make ownership, procurement or verification decisions without the right people involved',
        'Assume "someone else is handling it" means it never needs your attention',
      ] },
    ],
  },
  {
    id: 31, layout: 'timeline', module: 5,
    kicker: 'Module 05', title: 'Okiru’s B-BBEE learning pathway',
    steps: [
      { era: 'Half-day', title: 'Awareness (this course)', description: 'Baseline literacy for anyone in the organisation: why B-BBEE exists, the scorecard at a glance, and fronting awareness.' },
      { era: 'Full-day', title: 'B-BBEE Masterclass', description: 'For anyone who touches transformation data or decisions: the legislative landscape, every scorecard pillar in working detail, YES for Youth, and audit-ready implementation.' },
      { era: '3-day', title: 'B-BBEE Professional', description: 'For whoever will personally own the company’s B-BBEE compliance: full calculation mechanics, verification-audit preparation, governance structures and a 12-month improvement plan.' },
    ],
  },
  {
    id: 32, layout: 'exercise', module: 5,
    kicker: 'Module 05 · Activity', meta: 'Individual commitment', title: 'Name your next 30-day action',
    steps: [
      { title: 'Commit', detail: 'Write down one specific action you can personally take in the next 30 days that supports your organisation’s transformation performance or evidence trail.' },
    ],
    checkFor: 'If this half-day session changed how you think about even one decision this month, it has done its job.',
  },

  // ---- Closing ----
  {
    id: 33, layout: 'outcome',
    kicker: 'Next step', title: 'Leave with one honest action for the next 30 days',
    checks: [
      'You can explain, in plain terms, why B-BBEE exists',
      'You know your organisation’s broad category and what that means for its evidence',
      'You can name the seven scorecard elements and what each is trying to achieve',
      'You can spot a common myth and describe what fronting actually is',
      'You have written down one specific action for the next 30 days',
    ],
    footnote: 'If you want the calculation mechanics and audit-readiness detail, that is exactly what Okiru’s full-day B-BBEE Masterclass and 3-day B-BBEE Professional course are for.',
  },
];
